<script lang="ts" setup>
import TabView from 'primevue/tabview'
import TabPanel from 'primevue/tabpanel'
import { useRouter } from 'vue-router'
import PageHeader from '@/components/global/page-header/PageHeader.vue'
import MergeForm from '@/components/features/yarns/MergeForm.vue'
import { useMergeForm } from '@/composables/useMergeForm'
import Dialog from 'primevue/dialog'
import Button from 'primevue/button'
import { ref, computed, watch, onMounted } from 'vue'
import DeliverySection from '@/components/features/yarns/DeliverySectionTab.vue'
import UsageSection from '@/components/features/yarns/UsageSectionTab.vue'
import WastageSection from '@/components/features/yarns/WastageSectionTab.vue'
import { Icon } from '@iconify/vue'
import { archiveMerge, unarchiveMerge, getYarnById } from '@/api/yarns'

const router = useRouter()
const { values, loading, handleSubmit, setErrors, showToast, formType, resetForm } = useMergeForm('view')
const yarnId = router.currentRoute.value.params.id as string
const visible = ref(false)
const yarnData = ref<any>(null)

onMounted(async () => {
  try {
    yarnData.value = await getYarnById(yarnId)
  } catch (error) {
    console.error('Error loading yarn data:', error)
  }
})

const isArchived = computed(() => {
  const fromValues = values?.isArchived === true
  const fromDirectData = yarnData.value?.isArchived === true
  return fromValues || fromDirectData
})

const handleEditYarn = () => {
  router.push(`/yarns/${yarnId}/edit`)
}

const handleToggleArchive = async () => {
  try {
    if (isArchived.value) {
      await unarchiveMerge(yarnId)
      showToast('success', 'Merge Unarchived!', 'The merge has been successfully unarchived.')
      window.location.reload()
    } else {
      await archiveMerge(yarnId)
      showToast('success', 'Merge Archived!', 'The merge has been successfully archived.')
      router.push('/yarns/archieved')
    }
  } catch (error: any) {
    const action = isArchived.value ? 'Unarchive' : 'Archive'
    showToast('error', `${action} Failed!`, error.response?.data?.message || `Failed to ${action.toLowerCase()} merge`)
  }
}

const handleYarnUpdated = (updatedYarn: any) => {
  resetForm({
    values: { ...updatedYarn }
  })
}
</script>

<template>
  <PageHeader :title="values?.mergeNumber">
    <template #buttons>
      <div class="flex space-x-4">
        <Button
          outlined
          class="flex items-center gap-x-2"
          @click="handleToggleArchive"
        >
          <span>{{ isArchived ? 'Unarchive Merge' : 'Archive Merge' }}</span>
          <Icon :icon="isArchived ? 'material-symbols:unarchive' : 'material-symbols:archive'" class="text-xl" />
        </Button>
        <Button class="flex items-center gap-x-2" @click="handleEditYarn" >
          <span> Edit Merge </span>
          <Icon icon="basil:edit-solid" class="text-xl" />
        </Button>
      </div>
    </template>
  </PageHeader>

  <TabView class="bg-white rounded-md pb-2">
    <TabPanel header="Yarn Details">
      <MergeForm
        :type="formType"
        :values="values"
        :onSubmit="handleSubmit"
        :setErrors="setErrors"
      />
    </TabPanel>
    <TabPanel header="Delivery">
      <DeliverySection @yarnUpdated="handleYarnUpdated" />
    </TabPanel>
    <TabPanel header="Usage">
      <UsageSection />
    </TabPanel>
    <TabPanel header="Wastage">
      <WastageSection />
    </TabPanel>
  </TabView>
</template>
